import React, { useState } from "react";
import { X, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Button = ({ children, variant = "primary", size = "default", className = "", disabled, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-bold uppercase tracking-wide transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation select-none";
  
  const variants = {
    primary: "bg-orange-500 text-slate-900 hover:bg-orange-600 active:bg-orange-700",
    secondary: "bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700",
    outline: "border-2 border-slate-300 bg-transparent hover:bg-slate-100 active:bg-slate-200 text-slate-900",
    ghost: "hover:bg-slate-100 active:bg-slate-200 text-slate-700 hover:text-slate-900",
    destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800"
  };

  const sizes = {
    sm: "h-10 px-4 text-sm rounded-lg",
    default: "h-12 px-6 rounded-lg",
    lg: "h-14 px-8 text-lg rounded-lg",
    icon: "h-12 w-12 rounded-lg"
  };

  return (
    <button 
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Input Component


export const Input = ({ label, error, className = "", testId, ...props }) => (
  <div className="flex flex-col gap-1.5">
    {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
    <input
      className={`h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-4 text-base font-medium placeholder:text-slate-400 ${error ? 'border-red-500' : ''} ${className}`}
      data-testid={testId}
      {...props}
    />
    {error && <span className="text-sm text-red-600">{error}</span>}
  </div>
);

// Select Component


export const Select = ({ label, options, error, className = "", testId, ...props }) => (
  <div className="flex flex-col gap-1.5">
    {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
    <select
      className={`h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-lg px-4 text-base font-medium touch-manipulation ${error ? 'border-red-500' : ''} ${className}`}
      style={{ fontSize: '16px' }} /* Prevents iOS zoom on focus */
      data-testid={testId}
      {...props}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {error && <span className="text-sm text-red-600">{error}</span>}
  </div>
);

// Textarea Component


export const Textarea = ({ label, error, className = "", testId, ...props }) => (
  <div className="flex flex-col gap-1.5">
    {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
    <textarea
      className={`min-h-[100px] bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md p-4 text-base font-medium placeholder:text-slate-400 resize-y ${error ? 'border-red-500' : ''} ${className}`}
      data-testid={testId}
      {...props}
    />
    {error && <span className="text-sm text-red-600">{error}</span>}
  </div>
);

// Card Component


export const Card = ({ children, className = "", interactive = false, ...props }) => (
  <div 
    className={`bg-white border-2 border-slate-200 rounded-lg shadow-none p-6 ${interactive ? 'hover:border-orange-500/50 transition-colors cursor-pointer' : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Badge Component


export const Badge = ({ children, variant = "default", className = "", ...rest }) => {
  const variants = {
    default: "bg-slate-200 text-slate-800",
    success: "bg-green-600 text-white",
    warning: "bg-amber-500 text-white",
    error: "bg-red-600 text-white",
    info: "bg-blue-600 text-white",
    orange: "bg-orange-500 text-slate-900"
  };

  return (
    <span {...rest} className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-bold uppercase tracking-wide ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Modal Component


export const Modal = ({ isOpen, onClose, title, children, size = "default" }) => {
  if (!isOpen) return null;

  const sizes = {
    sm: "max-w-md",
    default: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-6xl"
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 overflow-y-auto" 
      onClick={onClose}
    >
      <div 
        className={`w-full ${sizes[size]} bg-white rounded-t-2xl sm:rounded-lg border-2 border-slate-200 shadow-xl animate-fade-in max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain`}
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      >
        <div className="flex items-center justify-between p-4 sm:p-6 border-b-2 border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight font-heading">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors touch-manipulation">
            <X className="w-6 h-6" />
          </button>
        </div>
        {/* pb-24 on mobile so the last input/button isn't hidden behind the
            soft keyboard or browser chrome; reverts to pb-6 on tablets+ */}
        <div className="p-4 sm:p-6 pb-24 sm:pb-6">{children}</div>
      </div>
    </div>
  );
};

// Tutorials Modal Component


export const Spinner = ({ size = "default" }) => {
  const sizes = { sm: "w-4 h-4", default: "w-8 h-8", lg: "w-12 h-12" };
  return (
    <div className={`${sizes[size]} border-4 border-slate-200 border-t-orange-500 rounded-full animate-spin`} />
  );
};

// Helper to open/download files (handles base64 data URLs and regular URLs)
const openFile = (fileUrl, fileName = 'document') => {
  if (!fileUrl) {
    alert('No file available');
    return;
  }
  
  // If it's a base64 data URL, create a download link
  if (fileUrl.startsWith('data:')) {
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      // Fallback: try opening in new window
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`<iframe src="${fileUrl}" style="width:100%;height:100%;border:none;"></iframe>`);
      } else {
        alert('Please allow popups to view this document');
      }
    }
  } else {
    // Regular URL - just open it
    window.open(fileUrl, '_blank');
  }
};

// File Upload Component - Direct upload from device


export const FileUploadInput = ({ label, accept = "*", onFileSelect, currentFile, onClear }) => {
  const [uploading, setUploading] = useState(false);
  
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        try {
          const res = await axios.post(`${API}/upload/file`, {
            file_data: base64Data,
            file_name: file.name,
            file_type: file.type
          }, { timeout: 120000, maxContentLength: Infinity, maxBodyLength: Infinity });
          onFileSelect(res.data.file_url, file.name);
        } catch (err) {
          const detail = err.response?.data?.detail
            || (err.response?.status === 413 ? "File too large — please use under 10 MB" : null)
            || (err.code === "ECONNABORTED" ? "Upload timed out — check your connection" : null)
            || err.message
            || "Unknown error";
          console.error("Upload failed:", err);
          alert(`Failed to upload file: ${detail}`);
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploading(false);
      alert("Error reading file");
    }
  };
  
  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-sm font-bold uppercase text-slate-600">{label}</label>}
      {currentFile ? (
        <div className="flex items-center gap-3 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-sm text-green-700 flex-1 truncate">{currentFile}</span>
          <Button variant="ghost" size="sm" onClick={onClear}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            disabled={uploading}
            className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 disabled:opacity-50"
          />
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
              <Spinner size="sm" />
              <span className="ml-2 text-sm text-slate-600">Uploading...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== LAYOUT COMPONENTS ====================

// Sidebar for Desktop


